import {
	Autocomplete,
	Box,
	Chip,
	CircularProgress,
	TextField,
	Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useSections } from "./hooks/useSections";
import { useTopSections } from "./hooks/useTopSections";
import type { Section, TopSection } from "./models/Section";

type Props = {
	value: Section[];
	onChange: (sections: Section[]) => void;
	topSectionsCount?: number;
	disabled?: boolean;
	label?: string;
};

export const SectionMultiSelect = ({
	value,
	onChange,
	topSectionsCount = 5,
	disabled = false,
	label = "Sections",
}: Props) => {
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState("");

	const { data: topSections = [], isLoading: loadingTop } = useTopSections({
		top: topSectionsCount,
	});

	const { data: options = [], isFetching: loading } = useSections({
		searchText: inputValue || null,
		enabled: open,
	});

	const handleChipClick = (section: TopSection) => {
		const isSelected = value.some((s) => s.SectionId === section.SectionId);
		if (isSelected) {
			onChange(value.filter((s) => s.SectionId !== section.SectionId));
		} else {
			onChange([...value, section]);
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "row",
				alignItems: "center",
				gap: 1,
				flexWrap: "wrap",
			}}
		>
			<Autocomplete
				multiple
				open={open}
				onOpen={() => setOpen(true)}
				onClose={() => setOpen(false)}
				value={value}
				onChange={(_, newValue) => onChange(newValue)}
				inputValue={inputValue}
				onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
				options={options}
				loading={loading}
				disabled={disabled}
				getOptionLabel={(option) => option.SectionName}
				isOptionEqualToValue={(option, val) =>
					option.SectionId === val.SectionId
				}
				limitTags={2}
				size="small"
				sx={{ minWidth: 200, flex: 1 }}
				renderOption={(props, option) => (
					<Box
						component="li"
						{...props}
						key={option.SectionId}
						sx={{ display: "flex", justifyContent: "space-between" }}
					>
						<Typography fontWeight={500}>{option.SectionName}</Typography>
					</Box>
				)}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						placeholder={value.length === 0 ? "All sections" : ""}
						size="small"
						slotProps={{
							input: {
								...params.InputProps,
								endAdornment: (
									<>
										{loading ? (
											<CircularProgress color="inherit" size={16} />
										) : null}
										{params.InputProps.endAdornment}
									</>
								),
							},
						}}
					/>
				)}
			/>
			{loadingTop ? (
				<CircularProgress size={14} />
			) : topSections.length > 0 ? (
				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						gap: 0.5,
						alignItems: "center",
					}}
				>
					<Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
						Top:
					</Typography>
					{topSections.map((section) => {
						const isSelected = value.some(
							(s) => s.SectionId === section.SectionId,
						);
						return (
							<Chip
								key={section.SectionId}
								label={section.SectionName}
								size="small"
								variant={isSelected ? "filled" : "outlined"}
								color={isSelected ? "primary" : "default"}
								onClick={() => handleChipClick(section)}
								disabled={disabled}
								sx={{
									cursor: "pointer",
									height: 22,
									"& .MuiChip-label": { px: 1, fontSize: "0.75rem" },
								}}
							/>
						);
					})}
				</Box>
			) : null}
		</Box>
	);
};
